import { useFacilityContext } from '@aspendental/shared-components-web';
import { Button, FormControl, FormHelperText, FormLabel, Grid, RadioGroup, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import {
	REASONS_FOR_VISIT,
	REASONS_FOR_VISIT_ES,
	REASON_FOR_VISIT_ID,
	REASON_FOR_VISIT_ID_ES,
} from './ReasonForVisit.helpers';
import ReasonForVisitButton from './ReasonForVisitButton';

import { DataLayerStepNames } from '@src/constants';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import {
	EventReasonForVisitValueEventTypeEnum,
	EventReasonResponseValueEventTypeEnum,
} from '@src/lib/__generated/user-events-api';
import { dataLayer } from '@src/services/dataLayer';
import { userEvent } from '@src/services/userEvent';
import { useAppStore, useReasonForVisitStore } from '@src/store';

export default function ReasonForVisit({
	webFacilityDetails,
}: {
	webFacilityDetails?: ReturnType<typeof useFacilityContext>;
}) {
	const [error, setError] = useState(false);
	const setReasonForVisit = useReasonForVisitStore((store) => store.setReasonForVisit);
	const reasonForVisit = useReasonForVisitStore((store) => store.reasonForVisit);
	const setHasReasonForVisit = useReasonForVisitStore((store) => store.setHasReasonForVisit);
	const { mottoFlow } = useAppStore((store) => store);
	const { t } = useTranslation('reasonForVisit');
	const router = useRouter();
	const { locale } = router;

	useEffect(() => {
		const logUserEvent = async () => {
			await userEvent({ event_type: EventReasonForVisitValueEventTypeEnum.ReasonForVisit });
		};
		// setTimeout prevents sessionId from not being set
		const timeout = setTimeout(() => {
			logUserEvent();
		});
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		if (webFacilityDetails?.officeInfo?.code && webFacilityDetails?.officeInfo.name) {
			dataLayer({
				language_preference: locale,
				brand: mottoFlow ? 'mottoaligners' : 'aspendental',
				step_name: DataLayerStepNames.reasonForVisit,
				website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
				website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
			});
		}
	}, [webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow]);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setReasonForVisit((event.target as HTMLInputElement).value as DigitalVisitReasonEnum);
		setError(false);
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!reasonForVisit) {
			setError(true);
		} else {
			setHasReasonForVisit(true);
			await userEvent({
				event_type: EventReasonResponseValueEventTypeEnum.ReasonForVisitResponse,
				event_data: {
					visit_reason: reasonForVisit,
				},
			});
			let reasonID;
			if (locale === 'es-US') {
				reasonID = REASON_FOR_VISIT_ID_ES[reasonForVisit];
			} else {
				reasonID = REASON_FOR_VISIT_ID[reasonForVisit];
			}
			dataLayer({
				language_preference: locale,
				brand: mottoFlow ? 'mottoaligners' : 'aspendental',
				step_name: DataLayerStepNames.reasonSelect,
				reason_for_visit: reasonForVisit,
				reason_for_visit_id: reasonID,
				...(webFacilityDetails?.officeInfo?.code && {
					website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
				}),
				...(webFacilityDetails?.officeInfo?.name && {
					website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
				}),
			});
		}
	};

	return (
		<form onSubmit={(e) => handleSubmit(e)}>
			<Stack alignItems="center" pt={4}>
				<FormControl sx={{ alignItems: 'center' }}>
					<Typography color="success.dark" variant="overline">
						{t('appointmentAssistant')}
					</Typography>

					<FormLabel id="visit-reason">
						<Typography color="primary" variant="header2">
							{t('reasonForVisitPrompt')}
						</Typography>
					</FormLabel>

					<RadioGroup
						aria-labelledby="visit-reason"
						onChange={handleChange}
						value={reasonForVisit}
						aria-invalid={error}
						aria-describedby={error ? 'visit-reason-error-message' : undefined}
					>
						{locale === 'en-US' && (
							<Grid
								container
								sx={{ maxWidth: '50rem' }}
								justifyContent="center"
								spacing={2.5}
								pt={6}
								px={2}
							>
								{REASONS_FOR_VISIT.map(({ value, icon, label }) => {
									const selected = value === reasonForVisit;
									return (
										<Grid item key={value} xs={6} md={4}>
											<ReasonForVisitButton
												value={value}
												icon={icon}
												label={label}
												selected={selected}
											/>
										</Grid>
									);
								})}
							</Grid>
						)}

						{locale === 'es-US' && (
							<Grid
								container
								sx={{ maxWidth: '50rem' }}
								justifyContent="center"
								spacing={2.5}
								pt={6}
								px={2}
							>
								{REASONS_FOR_VISIT_ES.map(({ value, icon, label }) => {
									const selected = value === reasonForVisit;
									return (
										<Grid item key={value} xs={6} md={4}>
											<ReasonForVisitButton
												value={value}
												icon={icon}
												label={label}
												selected={selected}
											/>
										</Grid>
									);
								})}
							</Grid>
						)}
					</RadioGroup>

					{error ? (
						<FormHelperText id="visit-reason-error-message" role="alert">
							<Typography variant="bodyMediumBook" color="error.dark">
								{t('noReasonSelected')}
							</Typography>
						</FormHelperText>
					) : null}

					<Button
						data-test-id="continue-button"
						variant="primaryDefault"
						size="M"
						sx={{ marginTop: '4rem' }}
						type="submit"
					>
						{t('continue')}
					</Button>
				</FormControl>
			</Stack>
		</form>
	);
}
